import React, { useMemo, useState } from "react";
import { monthTitle } from "./postStudioUtils";

export const maxFeaturedEvents = 15;
const typeLabels = { "public-holiday": "Public holiday", observance: "Festival / observance", national: "National day", international: "International day", commercial: "Shopping & social" };
const formatDay = (date, options = { weekday: "short", day: "numeric", month: "short" }) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, options);
const todayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const EventMeta = ({ event }) => <span className="studio-event-meta">
  <span className={`studio-event-type type-${event.type}`}>{typeLabels[event.type] || event.type}</span>
  {event.highlight && <span className="studio-event-important"><i className="ti ti-star-filled" aria-hidden="true" /> Important</span>}
  {event.tentative && <span className="studio-event-tentative" title="This date depends on the moon sighting and may shift by a day">Date may vary</span>}
</span>;

// Festivals, observances and special days for the chosen month and country.
// mode "single": pick one occasion for a dated post. mode "monthly": choose occasions to feature in the plan.
const CalendarEventsPanel = ({ mode, month, selectedDate, calendar, onCountryChange, onRetry, selectedEventId, onSelectEvent, featuredIds = [], onFeaturedChange }) => {
  const [importantOnly, setImportantOnly] = useState(false);
  const { loading, error, events = [], country, countryName, detectedCountry, availableCountries = [], live, stale } = calendar;
  const today = todayKey();
  const visible = useMemo(() => events.filter((event) => !importantOnly || event.highlight), [events, importantOnly]);
  const onDate = visible.filter((event) => event.date === selectedDate);
  const elsewhere = mode === "single" ? visible.filter((event) => event.date !== selectedDate) : visible;
  const toggleFeatured = (event) => onFeaturedChange(featuredIds.includes(event.id)
    ? featuredIds.filter((id) => id !== event.id)
    : featuredIds.length >= maxFeaturedEvents ? featuredIds : [...featuredIds, event.id]);

  return <section className="studio-events-panel" aria-label="Festivals and special days">
    <header className="studio-events-head">
      <div>
        <h3><i className="ti ti-confetti" aria-hidden="true" /> Festivals &amp; special days</h3>
        <p>{mode === "single" ? `For ${formatDay(selectedDate, { day: "numeric", month: "long", year: "numeric" })} and the rest of ${monthTitle(month)}` : `In ${monthTitle(month)}`}</p>
      </div>
      <div className="studio-events-controls">
        <label className="studio-events-country"><span>Holidays for</span>
          <select className="form-select" value={country || ""} onChange={(event) => onCountryChange(event.target.value)} disabled={loading && !events.length}>
            {availableCountries.map((item) => <option key={item.code} value={item.code}>{item.name}{item.code === detectedCountry ? " (your business)" : ""}</option>)}
            <option value="GLOBAL">International days only</option>
          </select>
        </label>
        <label className="studio-check"><input type="checkbox" checked={importantOnly} onChange={(event) => setImportantOnly(event.target.checked)} /> Important only</label>
      </div>
    </header>

    {loading ? <div className="studio-events-loading" role="status" aria-label="Loading festivals">{[0, 1, 2].map((item) => <span key={item} />)}</div>
      : error ? <div className="studio-events-empty" role="alert"><i className="ti ti-alert-triangle" aria-hidden="true" /> {error} <button type="button" className="studio-text-button" onClick={onRetry}>Try again</button></div>
      : <>
        {mode === "single" && <div className="studio-events-day">
          <h4>On this date</h4>
          {onDate.length ? <ul className="studio-event-cards">{onDate.map((event) => {
            const selected = selectedEventId === event.id;
            return <li key={event.id} className={`studio-event-card${selected ? " is-selected" : ""}${event.highlight ? " is-important" : ""}`}>
              <div><strong>{event.name}</strong><EventMeta event={event} /><p>{event.description}</p></div>
              <button type="button" className={`btn btn-sm ${selected ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={selected} onClick={() => onSelectEvent(selected ? null : event)}>
                {selected ? <><i className="ti ti-check" aria-hidden="true" /> Used for this post</> : "Use for this post"}
              </button>
            </li>;
          })}</ul> : <p className="studio-muted">No festivals or special days on this date{importantOnly ? " marked important" : ""}. Pick one below to move the post to that day.</p>}
        </div>}

        <div className="studio-events-month">
          <div className="studio-events-month-head">
            <h4>{mode === "single" ? `Other days in ${monthTitle(month)}` : `${visible.length} occasion${visible.length === 1 ? "" : "s"}`}</h4>
            {mode === "monthly" && <span className="studio-events-actions">
              <span>{featuredIds.length} featured</span>
              <button type="button" className="studio-text-button" onClick={() => onFeaturedChange(events.filter((event) => event.highlight && event.date >= today).slice(0, maxFeaturedEvents).map((event) => event.id))}>Select important</button>
              <button type="button" className="studio-text-button" disabled={!featuredIds.length} onClick={() => onFeaturedChange([])}>Clear</button>
            </span>}
          </div>
          {elsewhere.length ? <ul className="studio-event-list">{elsewhere.map((event) => {
            const past = event.date < today;
            const featured = featuredIds.includes(event.id);
            const content = <>
              <span className="studio-event-date"><strong>{formatDay(event.date, { day: "numeric" })}</strong><small>{formatDay(event.date, { month: "short" })}</small></span>
              <span className="studio-event-copy"><strong>{event.name}{event.highlight && <i className="ti ti-star-filled" aria-label="Important" />}</strong><small>{event.description}</small><EventMeta event={event} /></span>
            </>;
            return <li key={event.id} className={`${past ? "is-past" : ""}${featured ? " is-featured" : ""}`}>
              {mode === "monthly"
                ? <label className="studio-event-row"><input type="checkbox" checked={featured} disabled={past || (!featured && featuredIds.length >= maxFeaturedEvents)} onChange={() => toggleFeatured(event)} aria-label={`Feature ${event.name} on ${formatDay(event.date)}`} />{content}</label>
                : <button type="button" className="studio-event-row" disabled={past} onClick={() => onSelectEvent(event)} title={past ? "This date has passed" : `Create a post for ${event.name}`}>{content}<i className="ti ti-chevron-right" aria-hidden="true" /></button>}
            </li>;
          })}</ul> : <p className="studio-muted">{events.length ? "No other occasions match this filter." : `No festivals or special days found for ${monthTitle(month)}.`}</p>}
        </div>
      </>}

    <footer className="studio-events-source">
      <i className={`ti ${live ? "ti-world-check" : "ti-cloud-off"}`} aria-hidden="true" />
      {country === "GLOBAL" ? "International and global observances." : live
        ? `Public holidays and festivals for ${countryName} from Google's public holiday calendar, plus international days.`
        : stale ? `Showing the last saved holiday calendar for ${countryName}; live data is temporarily unavailable.`
          : `Live holidays for ${countryName || "your country"} are temporarily unavailable; showing international days only.`}
    </footer>
  </section>;
};

export default CalendarEventsPanel;
